// https://github.com/ueberdosis/tiptap/issues/3346#issuecomment-2447645565
import Document from '@tiptap/extension-document';
import HardBreak from '@tiptap/extension-hard-break';
import {
	type CommandProps,
	Extension,
	Editor,
	type EditorOptions,
} from '@tiptap/react';

export const InlineDocument = Document.extend({
	content: 'inline*',
});

export const LineBreakDocument = Document.extend({
	content: '(text | hardBreak)*',
});

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const nodeToTextWithLineBreaks = (node: any): string => {
	if (node.type === 'text') {
		return node.text || '';
	}
	if (node.type === 'hardBreak') {
		return '\n';
	}
	if (node.content && Array.isArray(node.content)) {
		return node.content.map(nodeToTextWithLineBreaks).join('');
	}
	return '';
};

export const editorContentToTextWithLineBreaks = (editor: Editor): string => {
	// Custom serialization that converts HardBreak nodes to \n
	const doc = editor.getJSON();
	return nodeToTextWithLineBreaks(doc);
};

export const lineBreaksToHardBreaks = (text: string): string => {
	if (!text) return '';

	return text.split('\n').join('<br>');
};

export const handlePasteConvertingNewLinesToHardBreaks: EditorOptions['editorProps']['handlePaste'] =
	(view, event) => {
		const text = event.clipboardData?.getData('text/plain');
		if (text && text.includes('\n')) {
			let { tr } = view.state;

			const lines = text.split('\n');
			for (let i = 0; i < lines.length; i++) {
				if (i > 0) {
					// Insert a hard break before each line (except the first line)
					tr = tr.insert(
						tr.selection.to,
						view.state.schema.nodes.hardBreak.create(),
					);
				}
				tr = tr.insertText(lines[i], tr.selection.to);
			}

			view.dispatch(tr);
			event.preventDefault();
			return true;
		}
		return false;
	};

declare module '@tiptap/core' {
	interface Commands<ReturnType> {
		shiftEnter: {
			/**
			 * Comments will be added to the autocomplete.
			 */
			sendToOnEnterAndClear: () => ReturnType;
		};
	}
}

export interface EnterOptions {
	onEnter: (text: string) => boolean;
}

export const Enter = Extension.create<EnterOptions>({
	name: 'EnterExtension',
	addOptions(): EnterOptions {
		return { onEnter: () => false };
	},
	addCommands() {
		return {
			sendToOnEnterAndClear:
				() =>
				({ editor, commands }: CommandProps) => {
					const doc = editor.getJSON();
					const text = nodeToTextWithLineBreaks(doc);
					const didSend = this.options.onEnter(text);
					if (didSend) {
						commands.clearContent();
					}
					return true;
				},
		};
	},
	addKeyboardShortcuts() {
		return {
			Enter: ({ editor }) => {
				editor.commands.sendToOnEnterAndClear();
				return true;
			},
		};
	},
});

export const LineBreakHardBreak = HardBreak.extend({
	addKeyboardShortcuts() {
		return {
			Enter: () => this.editor.commands.insertContent('<br>'),
			'Shift-Enter': () => this.editor.commands.insertContent('<br>'),
		};
	},
});
